import { useNavigate } from "@tanstack/react-router";
import { ArrowRight, LayoutGrid, List, Plus, Search, ShieldAlert } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { BudgetLineHistorySheet } from "@/components/buy-log/BudgetLineHistorySheet";
import { BuyLogAddLineItemDialog } from "@/components/buy-log/BuyLogAddLineItemDialog";
import { BuyLogGrid } from "@/components/buy-log/BuyLogGrid";
import { BuyLogHierarchy } from "@/components/buy-log/BuyLogHierarchy";
import type { BuyLogRowPatch } from "@/components/buy-log/BuyLogRow";
import { BuyLogSummaryBar } from "@/components/buy-log/BuyLogSummaryBar";
import { CostCodeBrowser } from "@/components/buy-log/CostCodeBrowser";
import { DeleteBudgetLineDialog } from "@/components/buy-log/DeleteBudgetLineDialog";
import { ErrorState } from "@/components/shared/error-state";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import type { BudgetLineItem, SaveBudgetLineItemPayload } from "@/hooks/use-budgets";
import {
  computeBuyLogSummary,
  useDeleteBudgetLineItem,
  useProjectBudget,
  useProjectCostCodes,
  useSaveProjectBudget,
} from "@/hooks/use-budgets";
import { usePendingBuyouts } from "@/hooks/use-change-orders";
import { useCompanies } from "@/hooks/use-companies";
import type { ProjectLifecyclePhase } from "@/hooks/use-projects";
import {
  nextProjectPhase,
  PROJECT_PHASE_LABELS,
  useAdvanceProjectPhase,
  useProject,
} from "@/hooks/use-projects";
import { useSession } from "@/lib/auth-client";

type ViewMode = "hierarchy" | "grid" | "browser";

/** User types permitted to edit while the project is in Pre-Construction. */
const PRECON_WRITERS = new Set(["admin", "executive", "project_manager", "estimator"]);
/** User types permitted to edit after Pre-Construction (admin-only). */
const POST_PRECON_WRITERS = new Set(["admin", "executive"]);

function canEditForPhase(
  userType: string | null | undefined,
  role: string | undefined,
  phase: ProjectLifecyclePhase,
): boolean {
  const gate = phase === "pre_construction" ? PRECON_WRITERS : POST_PRECON_WRITERS;
  if (userType) return gate.has(userType);
  if (phase !== "pre_construction") return role === "admin";
  return role === "admin" || role === "project_manager";
}

function isAdminLike(userType: string | null | undefined, role: string | undefined): boolean {
  if (userType) return userType === "admin" || userType === "executive";
  return role === "admin";
}

/**
 * Normalize a BudgetLineItem back into the flat upsert payload that
 * `POST /api/projects/:id/budget` expects. Used for inline field edits.
 */
function toSavePayload(item: BudgetLineItem, patch: BuyLogRowPatch): SaveBudgetLineItemPayload {
  return {
    id: item.id,
    costCodeId: item.costCodeId,
    description: item.description,
    csiDivision: item.csiDivision,
    originalBudgetedAmt: patch.originalBudgetedAmt ?? item.originalBudgetedAmt,
    awardValue: patch.awardValue ?? item.awardValue,
    allowance: patch.allowance ?? item.allowance,
    holdsAtBuy: patch.holdsAtBuy ?? item.holdsAtBuy,
    awardedCompanyId:
      patch.awardedCompanyId === undefined ? item.awardedCompanyId : patch.awardedCompanyId,
    isAllowance: patch.isAllowance === undefined ? item.isAllowance : patch.isAllowance,
    scheduledValueText:
      patch.scheduledValueText === undefined ? item.scheduledValueText : patch.scheduledValueText,
    sortOrder: item.sortOrder,
  };
}

export function BuyLogTab({ projectId }: { projectId: string }) {
  const { data: sessionData } = useSession();
  const user = sessionData?.user as { role?: string; userType?: string | null } | undefined;
  const navigate = useNavigate();

  const project = useProject(projectId);
  const phase: ProjectLifecyclePhase = project.data?.phase ?? "pre_construction";
  const canEdit = canEditForPhase(user?.userType, user?.role, phase);
  const isAdmin = isAdminLike(user?.userType, user?.role);
  const canBrowseCostCodes = user?.role !== "contractor";
  const upcomingPhase = nextProjectPhase(phase);

  const budget = useProjectBudget(projectId);
  const costCodes = useProjectCostCodes(projectId);
  const companies = useCompanies();

  const saveMutation = useSaveProjectBudget(projectId);
  const deleteMutation = useDeleteBudgetLineItem(projectId);
  const advanceMutation = useAdvanceProjectPhase(projectId);

  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [addOpen, setAddOpen] = useState(false);
  const [historyLine, setHistoryLine] = useState<BudgetLineItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<BudgetLineItem | null>(null);
  const [advanceConfirmOpen, setAdvanceConfirmOpen] = useState(false);

  // When the user returns from the "Create Commitment" page, briefly highlight
  // the source Buy Log row so they can see which line they just acted on.
  // The handoff happens via sessionStorage (see commitment-new.tsx). Clears
  // itself after 6s so the highlight isn't sticky.
  const [recentlyCommittedLineId, setRecentlyCommittedLineId] = useState<string | null>(null);
  useEffect(() => {
    const id = sessionStorage.getItem("buyLogRecentlyCommittedLineId");
    if (!id) return;
    sessionStorage.removeItem("buyLogRecentlyCommittedLineId");
    setRecentlyCommittedLineId(id);
    const timer = setTimeout(() => setRecentlyCommittedLineId(null), 6000);
    return () => clearTimeout(timer);
  }, []);

  const lineItems = budget.data?.lineItems ?? [];
  const summary = useMemo(() => computeBuyLogSummary(lineItems), [lineItems]);

  // PCOs awaiting buyout (US-125 AC #10) — surface them so the PM sees what
  // cost codes still need a vendor before the change order can execute.
  const pendingBuyoutsQuery = usePendingBuyouts(projectId);
  const pendingBuyouts = pendingBuyoutsQuery.data ?? [];

  const subcontractorCompanies = useMemo(() => {
    const allowed = new Set([
      "subcontractor",
      "vendor",
      "contractor",
      "general_contractor",
      "material_supplier",
    ]);
    return (companies.data ?? []).filter((c) =>
      c.type ? allowed.has(c.type) || c.type === "contractor" : true,
    );
  }, [companies.data]);

  function handlePatchLine(lineId: string, patch: BuyLogRowPatch) {
    const item = lineItems.find((l) => l.id === lineId);
    if (!item) return;
    saveMutation.mutate({ lineItems: [toSavePayload(item, patch)] });
  }

  function handleAddLine(data: {
    costCodeId: string;
    description: string;
    csiDivision: string | null;
    originalBudgetedAmt: string;
    isAllowance: boolean;
    scheduledValueText: string | null;
  }) {
    saveMutation.mutate(
      {
        lineItems: [
          {
            costCodeId: data.costCodeId,
            description: data.description,
            csiDivision: data.csiDivision,
            originalBudgetedAmt: data.originalBudgetedAmt,
            isAllowance: data.isAllowance,
            scheduledValueText: data.scheduledValueText,
            sortOrder: lineItems.length,
          },
        ],
      },
      {
        onSuccess: () => setAddOpen(false),
      },
    );
  }

  function handleConfirmDelete() {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id, {
      onSuccess: () => setDeleteTarget(null),
      onError: () => {
        // DeleteBudgetLineDialog reads the mutation's error to render any
        // blockingCommitments payload; keep the dialog open so the user can
        // see the linked commitments and decide next steps.
      },
    });
  }

  if (budget.isLoading || project.isLoading) return <Skeleton className="h-96 w-full" />;
  if (budget.isError) {
    return <ErrorState message={budget.error?.message} onRetry={() => budget.refetch()} />;
  }

  const budgetLocked = phase !== "pre_construction";

  return (
    <div className="space-y-4">
      <BuyLogSummaryBar summary={summary} />

      {pendingBuyouts.length > 0 ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShieldAlert className="h-4 w-4 text-amber-700" />
              <span className="text-sm font-semibold text-amber-900">
                Pending Buyout — {pendingBuyouts.length} PCO
                {pendingBuyouts.length === 1 ? "" : "s"}
              </span>
            </div>
            <span className="text-xs text-amber-800">
              Buy out a sub for each cost code below before the PCO can be approved.
            </span>
          </div>
          <ul className="mt-2 space-y-1 text-sm">
            {pendingBuyouts.map((b) => (
              <li
                key={b.id}
                className="flex items-center justify-between rounded-md bg-white border border-amber-100 px-3 py-1.5"
              >
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs text-amber-900">{b.commitmentNumber}</span>
                  <span className="text-gray-900">{b.title}</span>
                  {b.costCode ? (
                    <span className="text-xs text-gray-600">
                      {b.costCode}
                      {b.costCodeDescription ? ` — ${b.costCodeDescription}` : ""}
                    </span>
                  ) : null}
                </div>
                <span className="text-xs text-gray-500">
                  {new Date(b.createdAt).toLocaleDateString()}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {budgetLocked ? (
        <div className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
          <ShieldAlert className="h-4 w-4 text-amber-700" />
          <div className="flex-1 text-sm text-amber-900">
            <span className="font-semibold">Budget locked.</span> This project is in{" "}
            <strong>{PROJECT_PHASE_LABELS[phase]}</strong>. Budget edits are restricted to admins —
            PMs and Lead Estimators can only modify the budget during Pre-Construction.
          </div>
        </div>
      ) : null}

      <div className="flex items-center justify-between">
        <div className="inline-flex items-center gap-2">
          <div className="inline-flex items-center rounded-lg border border-gray-200 bg-white p-0.5 shadow-sm">
            <button
              type="button"
              onClick={() => setViewMode("hierarchy")}
              className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm ${
                viewMode === "hierarchy"
                  ? "bg-gray-900 text-white"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              <List className="h-3.5 w-3.5" />
              Hierarchy
            </button>
            <button
              type="button"
              onClick={() => setViewMode("grid")}
              className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm ${
                viewMode === "grid" ? "bg-gray-900 text-white" : "text-gray-600 hover:text-gray-900"
              }`}
            >
              <LayoutGrid className="h-3.5 w-3.5" />
              Grid
            </button>
            {canBrowseCostCodes ? (
              <button
                type="button"
                onClick={() => setViewMode("browser")}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm ${
                  viewMode === "browser"
                    ? "bg-gray-900 text-white"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                <Search className="h-3.5 w-3.5" />
                Cost Code Browser
              </button>
            ) : null}
          </div>
          <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700">
            {PROJECT_PHASE_LABELS[phase]}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && upcomingPhase ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setAdvanceConfirmOpen(true)}
              className="rounded-lg"
            >
              <ArrowRight className="mr-1.5 h-4 w-4" />
              Advance to {PROJECT_PHASE_LABELS[upcomingPhase]}
            </Button>
          ) : null}
          {canEdit && viewMode !== "browser" ? (
            <Button size="sm" onClick={() => setAddOpen(true)} className="rounded-lg">
              <Plus className="mr-1.5 h-4 w-4" />
              Add Line Item
            </Button>
          ) : null}
        </div>
      </div>

      {/* Cost Code Browser — always mounted to preserve search/filter state across tab switches */}
      <div className={viewMode === "browser" ? "" : "hidden"}>
        <CostCodeBrowser
          projectId={projectId}
          costCodes={costCodes.data ?? []}
          lineItems={lineItems}
          canEdit={canEdit}
          isVisible={viewMode === "browser"}
        />
      </div>

      {viewMode !== "browser" && lineItems.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white p-12 text-center">
          <p className="text-sm text-gray-500">No Buy Log line items yet.</p>
          {canEdit ? (
            <Button variant="outline" size="sm" onClick={() => setAddOpen(true)} className="mt-3">
              <Plus className="mr-1.5 h-4 w-4" />
              Add the first line
            </Button>
          ) : null}
        </div>
      ) : viewMode === "hierarchy" ? (
        <BuyLogHierarchy
          lineItems={lineItems}
          companies={subcontractorCompanies}
          canEdit={canEdit}
          onPatchLine={handlePatchLine}
          onDeleteLine={(id) => {
            const item = lineItems.find((l) => l.id === id);
            if (item) setDeleteTarget(item);
          }}
          onCreateCommitment={(lineItem) =>
            navigate({
              to: "/commitments/new",
              search: { projectId, budgetLineId: lineItem.id },
            })
          }
          onViewHistory={setHistoryLine}
          onViewCommitment={(commitmentId) =>
            navigate({ to: "/commitments/$id", params: { id: commitmentId } })
          }
          recentlyCommittedLineId={recentlyCommittedLineId}
        />
      ) : viewMode === "grid" ? (
        <BuyLogGrid
          lineItems={lineItems}
          companies={subcontractorCompanies}
          canEdit={canEdit}
          onPatchLine={handlePatchLine}
          onDeleteLine={(id) => {
            const item = lineItems.find((l) => l.id === id);
            if (item) setDeleteTarget(item);
          }}
          onCreateCommitment={(lineItem) =>
            navigate({
              to: "/commitments/new",
              search: { projectId, budgetLineId: lineItem.id },
            })
          }
          onViewHistory={setHistoryLine}
          onViewCommitment={(commitmentId) =>
            navigate({ to: "/commitments/$id", params: { id: commitmentId } })
          }
          recentlyCommittedLineId={recentlyCommittedLineId}
        />
      ) : null}

      <BuyLogAddLineItemDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        costCodes={costCodes.data ?? []}
        hasGeneralContractor={!!project.data?.generalContractorCompanyId}
        onSubmit={handleAddLine}
        isPending={saveMutation.isPending}
      />

      <BudgetLineHistorySheet
        projectId={projectId}
        lineItem={historyLine}
        open={historyLine !== null}
        onOpenChange={(open) => {
          if (!open) setHistoryLine(null);
        }}
      />

      <DeleteBudgetLineDialog
        lineItem={deleteTarget}
        error={deleteMutation.error}
        isPending={deleteMutation.isPending}
        onConfirm={handleConfirmDelete}
        onClose={() => {
          setDeleteTarget(null);
          deleteMutation.reset();
        }}
      />

      <AlertDialog open={advanceConfirmOpen} onOpenChange={setAdvanceConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Advance to {upcomingPhase ? PROJECT_PHASE_LABELS[upcomingPhase] : "next phase"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {upcomingPhase === "construction"
                ? "Advancing to Construction locks the budget. Only admins will be able to modify line items from this point."
                : upcomingPhase
                  ? `The project will move to ${PROJECT_PHASE_LABELS[upcomingPhase]}.`
                  : "There is no further phase to advance to."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!upcomingPhase) return;
                advanceMutation.mutate(upcomingPhase, {
                  onSuccess: () => {
                    setAdvanceConfirmOpen(false);
                    project.refetch();
                  },
                  onError: (err: Error) => toast.error(err.message),
                });
              }}
              disabled={advanceMutation.isPending || !upcomingPhase}
            >
              {advanceMutation.isPending ? "Advancing…" : "Advance"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
